/** @file authMethods: password login (session cookie), logout, me, and the owner's bearer tokens. */
import type { MeReply, MintBody, MintedToken, TokenInfo, TokenListReply } from '../types/restAdded';
import type { ClientOptions } from './clientOptions';
import { fillPath } from './fillPath';
import type { HeadersLike } from './webTypes';
import { request } from './request';

/** The auth members of the client. */
export type AuthMethods = {
  /** `POST /api/v1/auth/login`; `cookie` is the `__Host-session` value, null where `Set-Cookie` is hidden (browsers). */
  login: (password: string) => Promise<{ cookie: string | null; me: MeReply }>;
  /** `POST /api/v1/auth/logout`. */
  logout: () => Promise<void>;
  /** `GET /api/v1/auth/me`. */
  me: () => Promise<MeReply>;
  tokens: {
    /** `POST /api/v1/auth/tokens`; the secret is in the reply only. */
    mint: (body: MintBody) => Promise<MintedToken>;
    /** `GET /api/v1/auth/tokens`. */
    list: () => Promise<TokenInfo[]>;
    /** `DELETE /api/v1/auth/tokens/{tokenId}`. */
    revoke: (tokenId: string) => Promise<void>;
  };
};

function sessionCookie(headers: HeadersLike): string | null {
  const match = /(?:^|[;,]\s*)__Host-session=([^;,]*)/.exec(headers.get('set-cookie') ?? '');
  return match?.[1] ?? null;
}

/** Auth calls over `opts`; login works without credentials, the rest need a session or token. */
export function authMethods(opts: ClientOptions): AuthMethods {
  return {
    login: async (password) => {
      const r = await request<MeReply>(opts, { method: 'POST', path: '/api/v1/auth/login', body: { password }, accept: 'json' });
      return { cookie: sessionCookie(r.headers), me: r.value };
    },
    logout: async () => { await request(opts, { method: 'POST', path: '/api/v1/auth/logout', accept: 'empty' }); },
    me: async () => (await request<MeReply>(opts, { method: 'GET', path: '/api/v1/auth/me', accept: 'json' })).value,
    tokens: {
      mint: async (body) => (await request<MintedToken>(opts, { method: 'POST', path: '/api/v1/auth/tokens', body, accept: 'json' })).value,
      list: async () => (await request<TokenListReply>(opts, { method: 'GET', path: '/api/v1/auth/tokens', accept: 'json' })).value.tokens,
      revoke: async (tokenId) => {
        await request(opts, { method: 'DELETE', path: fillPath('/api/v1/auth/tokens/{tokenId}', { tokenId }), accept: 'empty' });
      },
    },
  };
}
